import { KeyboardEvent, MouseEvent, PropsWithChildren, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import styles from "./Modal.styles";

type TModalMaxWidth = DialogProps["maxWidth"] | "xxs";

export type TModalProps = Pick<
  DialogProps,
  "children" | "scroll" | "sx" | "title" | "fullWidth" | "onClick" | "onKeyDown"
> & {
  open: boolean;
  maxWidth?: TModalMaxWidth;
  subTitle?: string | undefined;
  header?: ReactNode;
  actions?: ReactNode;
};

const Modal = ({
  sx,
  title,
  subTitle,
  header,
  actions,
  maxWidth,
  ...props
}: PropsWithChildren<TModalProps>) => {
  const combinedStyles = {
    ...styles.dialog,
    ...(maxWidth === "xxs" ? styles.xxsDialog : {}),
    ...sx,
  };

  if (!props.open) {
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    props.onClick?.(event);
    event.stopPropagation();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    props.onKeyDown?.(event);
    event.stopPropagation();
  };

  return (
    <Dialog
      sx={combinedStyles}
      {...props}
      maxWidth={maxWidth === "xxs" ? false : maxWidth}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title && (
            <DialogTitle width="100%">
              {title}
              {subTitle && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "flex" }}
                >
                  {subTitle}
                </Typography>
              )}
            </DialogTitle>
          )}
        </Stack>
        {header}
      </Stack>
      {props.children ? <DialogContent>{props.children}</DialogContent> : null}
      {actions}
    </Dialog>
  );
};

export default Modal;
